import { Button, Form, Input, Space } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { useNotify } from '@/api/notify';
import { AuthLayout } from '@/components/AuthLayout';
import type { SignedInUser } from '@core/session/types';

interface ChangePassword {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Setting your own password — the screen that makes `MustChangePassword` satisfiable, and the one
 * the template calls "reset password".
 *
 * A user created by an administrator gets a one-time password and `MustChangePassword` set, and
 * the API's permission handler then refuses every permission-protected endpoint with
 * `Auth_PasswordChangeRequired` until it is cleared. Without this screen that state had no exit:
 * the account signed in, the shell rendered, and every screen behind it answered 403.
 *
 * It wears the template's `resetPasswordIllustration` layout — gradient panel with a lock on the
 * left, the fields on the canvas on the right — through the shared `AuthLayout`, so it and the
 * sign-in screen are visibly one flow. Two things the template's version has are deliberately not
 * here. It offers *only* "New password" and "Confirm new password", because it imagines arriving
 * from an emailed reset link; AttireRent has no such link (nothing issues a
 * `UserVerificationToken` for password reset yet), so the current password is what authorises the
 * change and the field stays. And its submit navigates to the login screen unconditionally, which
 * is a mock — here it posts, and the caller decides what a success means.
 *
 * Rendered outside `AppShell` rather than as a registry page, in both modes. It is not a menu
 * destination — the menu is a projection of permissions, and this is the one thing a user with no
 * permissions at all must still be able to reach.
 *
 * @param forced
 * True when the account is on a one-time password. There is then no way past this screen except
 * changing the password or signing out, which is the whole point of the flag — so the cancel
 * action becomes "Sign out" rather than a way back into an app that would refuse every request.
 */
export function ChangePasswordPage({
  user,
  forced,
  onChanged,
  onCancel,
}: {
  user: SignedInUser;
  forced: boolean;
  onChanged: () => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  const submit = async (values: ChangePassword) => {
    setBusy(true);

    try {
      await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
      });

      onChanged();
    } catch (caught) {
      notify.error(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      panel={{
        icon: 'ph-duotone ph-lock-key',
        heading: forced ? 'Choose your password' : 'Reset your password',
        blurb: forced
          ? 'The one-time password your administrator gave you works once. Pick your own to carry on.'
          : 'Choose a strong, secure password. You will be signed out everywhere once you do.',
      }}
      title={forced ? 'Choose a password' : 'Change password'}
      subtitle={
        forced ? (
          <>
            You signed in with a one-time password. Choose your own to continue as{' '}
            <strong className="text-title">{user.username}</strong>.
          </>
        ) : (
          <>
            Signed in as <strong className="text-title">{user.username}</strong>.
          </>
        )
      }
    >
      <Form<ChangePassword>
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => void submit(values)}
      >
        <Form.Item
          label={forced ? 'One-time password' : 'Current password'}
          name="currentPassword"
          rules={[{ required: true, message: 'Enter your current password.' }]}
        >
          <Input.Password size="large" autoComplete="current-password" autoFocus />
        </Form.Item>

        {/*
          No length or character rules are stated here. The policy is server configuration
          (`Identity:PasswordPolicy`) and can differ per deployment, so a copy in this form
          would drift into telling people a rule the API is not applying. The server reports
          the first unmet rule, already localized, and the failure toast shows it verbatim.
          What is checked here is only what the client can know by itself.
        */}
        <Form.Item
          label="New password"
          name="newPassword"
          dependencies={['currentPassword']}
          rules={[
            { required: true, message: 'Enter a new password.' },
            ({ getFieldValue }) => ({
              validator: (_, value: string) =>
                !value || value !== getFieldValue('currentPassword')
                  ? Promise.resolve()
                  : Promise.reject(new Error('The new password must differ from the current one.')),
            }),
          ]}
        >
          <Input.Password size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Confirm new password"
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Type the new password again.' },
            ({ getFieldValue }) => ({
              validator: (_, value: string) =>
                !value || value === getFieldValue('newPassword')
                  ? Promise.resolve()
                  : Promise.reject(new Error('The two passwords do not match.')),
            }),
          ]}
        >
          <Input.Password size="large" autoComplete="new-password" />
        </Form.Item>

        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Button type="primary" htmlType="submit" size="large" loading={busy} block>
            {forced ? 'Set password' : 'Change password'}
          </Button>

          {/*
            The template draws its way out as a "← Back to Sign In" link. Here the same slot is a
            button because what it does differs: on a forced change there is no session to go back
            to, so it signs the user out rather than pretending there is a screen behind this one.
          */}
          <Button size="large" block onClick={onCancel} disabled={busy}>
            {forced ? 'Sign out' : 'Cancel'}
          </Button>
        </Space>
      </Form>

      <p className="mt-3 rounded-md border border-border-color bg-white px-3 py-2 text-center text-xs text-default">
        Changing your password signs you out everywhere, including here.
      </p>
    </AuthLayout>
  );
}
